import {
  Accessor,
  createEffect,
  createSignal,
  on,
  onCleanup,
  onMount,
} from 'solid-js';
import {
  computeViewportAnchoredPosition,
  measureFloatingDimensions,
  ViewportAnchoredPosition,
} from './viewport-anchored-position';

export interface UseViewportAnchoredPositionOptions {
  readonly isOpen: Accessor<boolean>;
  readonly margin?: number;
  readonly estimatedFloatingSize?: {
    readonly width: number;
    readonly height: number;
  };
}

export interface UseViewportAnchoredPositionResult {
  readonly position: Accessor<ViewportAnchoredPosition>;
  setAnchorElement(element: HTMLElement | undefined): void;
  setFloatingElement(element: HTMLElement | undefined): void;
  schedulePositionUpdate(): void;
}

export function useViewportAnchoredPosition(
  options: UseViewportAnchoredPositionOptions
): UseViewportAnchoredPositionResult {
  const [position, setPosition] = createSignal<ViewportAnchoredPosition>({
    left: 0,
    top: 0,
  });

  let anchorElement: HTMLElement | undefined;
  let floatingElement: HTMLElement | undefined;
  let outerPositionFrameId: number | undefined;
  let innerPositionFrameId: number | undefined;
  let isMounted = false;

  function updatePosition(): void {
    if (!isMounted || !options.isOpen() || !anchorElement) {
      return;
    }

    const { width, height } = measureFloatingDimensions(
      floatingElement,
      options.estimatedFloatingSize
    );

    setPosition(
      computeViewportAnchoredPosition({
        anchorRect: anchorElement.getBoundingClientRect(),
        floatingWidth: width,
        floatingHeight: height,
        margin: options.margin,
      })
    );
  }

  function cancelPendingPositionFrames(): void {
    if (outerPositionFrameId !== undefined) {
      cancelAnimationFrame(outerPositionFrameId);
      outerPositionFrameId = undefined;
    }
    if (innerPositionFrameId !== undefined) {
      cancelAnimationFrame(innerPositionFrameId);
      innerPositionFrameId = undefined;
    }
  }

  function schedulePositionUpdate(): void {
    cancelPendingPositionFrames();
    // Position immediately so the portal does not flash at a stale or zero origin.
    updatePosition();
    outerPositionFrameId = requestAnimationFrame(() => {
      outerPositionFrameId = undefined;
      updatePosition();
      // Floating content may layout after the portal wrapper mounts.
      innerPositionFrameId = requestAnimationFrame(() => {
        innerPositionFrameId = undefined;
        updatePosition();
      });
    });
  }

  onMount(() => {
    isMounted = true;
    window.addEventListener('resize', schedulePositionUpdate);
    window.addEventListener('scroll', schedulePositionUpdate, true);
  });

  onCleanup(() => {
    isMounted = false;
    cancelPendingPositionFrames();
    window.removeEventListener('resize', schedulePositionUpdate);
    window.removeEventListener('scroll', schedulePositionUpdate, true);
  });

  createEffect(
    on(options.isOpen, (isOpen) => {
      if (isOpen) {
        schedulePositionUpdate();
      }
    })
  );

  return {
    position,
    setAnchorElement(element: HTMLElement | undefined): void {
      anchorElement = element;
    },
    setFloatingElement(element: HTMLElement | undefined): void {
      floatingElement = element;
      if (element) {
        schedulePositionUpdate();
      }
    },
    schedulePositionUpdate,
  };
}
